import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import clsx from 'clsx'
import Cursor from 'components/Icons/Cursor'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import AppIcon from '../preview/AppIcon'
import './howItWorksDemo.scss'
import { useHowItWorksDemo } from './useHowItWorksDemo'

const t = buildTranslate('live_updates.overview.how')
const tDemo = buildTranslate('live_updates.overview.how.demo')
const tTracker = buildTranslate('live_updates.preview.tracker')
const tInAppStages = buildTranslate('live_updates.preview.in_app_stages')
const tPublish = buildTranslate('live_updates')

const STEP_KEYS = ['step1', 'step2', 'step3'] as const

const STEP_GAP = '14px'
const STEP_MIN_WIDTH = '260px'
// Tall enough for the longest step's title + two-line body, so all three demo
// cards start on the same line (Sketch prototype `.how .step .sx`).
const STEP_COPY_HEIGHT = '68px'

const MiniCardHeader = ({ appIconUrl }: { appIconUrl: string | undefined }) => (
  <div className="hiw-top">
    <AppIcon appIconUrl={appIconUrl} size="22px" radius="5px" />
    <Text.BodyText5 bold className="hiw-eta">
      {tDemo('eta')}
    </Text.BodyText5>
  </div>
)

// Step 1: a mini status card whose two later stages toggle on as a
// self-playing cursor "clicks" them, driven by useHowItWorksDemo's timer
// sequence (Sketch prototype live-updates-onboarding v29).
type DemoProps = {
  demo: ReturnType<typeof useHowItWorksDemo>
  appIconUrl: string | undefined
}

const Step1Demo = ({ demo, appIconUrl }: DemoProps) => {
  const { onTheWayOn, deliveredOn, hovered, fillPct, cursor } = demo.step1
  const rows = [
    { key: 'placed', on: true },
    { key: 'on_the_way', on: onTheWayOn },
    { key: 'delivered', on: deliveredOn },
  ] as const

  return (
    <div className="hiw-card hiw-combo">
      <MiniCardHeader appIconUrl={appIconUrl} />
      <Text.BodyText4 tag="p" bold className="hiw-status">
        {tInAppStages('placed')}
      </Text.BodyText4>
      <Text.BodyText5 className="hiw-sub">{tDemo('sub_placed')}</Text.BodyText5>
      <div className="hiw-tl">
        <span className="hiw-track" />
        <span className="hiw-fill" style={{ width: `${fillPct}%` }} />
        {rows.map(row => (
          <div key={row.key} className={`hiw-stage ${row.on ? 'on' : ''}`}>
            <span className="hiw-dot" />
            <span className="hiw-stage-label">{tTracker(row.key)}</span>
          </div>
        ))}
      </div>
      <div className="hiw-tglist">
        {rows.map(row => (
          <div key={row.key} className="hiw-tgrow">
            <span className="hiw-tgrow-name">{tTracker(row.key)}</span>
            <span
              className={clsx('hiw-sw', {
                on: row.on,
                hover: row.key === hovered,
              })}
            />
          </div>
        ))}
      </div>
      {cursor.visible && (
        <span
          aria-hidden="true"
          className={`hiw-cursor ${cursor.clicking ? 'click' : ''}`}
          style={{ left: `${cursor.leftPct}%`, top: `${cursor.topPx}px` }}
        >
          <Cursor fillColor={palette.grey90} />
        </span>
      )}
    </div>
  )
}

// Step 2: the same mini card, typing an example message into the live-
// update-text field with a blinking caret.
const Step2Demo = ({ demo, appIconUrl }: DemoProps) => {
  const { typed, done } = demo.step2

  return (
    <div className="hiw-card">
      <MiniCardHeader appIconUrl={appIconUrl} />
      <Text.BodyText4 tag="p" bold className="hiw-status">
        {tInAppStages('preparing')}
      </Text.BodyText4>
      <div className="hiw-field">
        <Text.BodyText5 className="hiw-field-label">
          {tDemo('field_label')}
        </Text.BodyText5>
        <div className={`hiw-typewrap ${done ? 'done' : ''}`}>
          {typed}
          {!done && <span className="hiw-caret" />}
        </div>
      </div>
      <div className="hiw-tl">
        <span className="hiw-track" />
        <span className="hiw-fill" style={{ width: '50%' }} />
        {(['placed', 'preparing', 'ready'] as const).map(key => (
          <div key={key} className={`hiw-stage ${key !== 'ready' ? 'on' : ''}`}>
            <span className="hiw-dot" />
            <span className="hiw-stage-label">{tTracker(key)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Step 3: a Publish button that presses, spins, then confirms.
const Step3Demo = ({ demo }: DemoProps) => {
  const { phase } = demo.step3

  return (
    <div className="hiw-pubwrap">
      {/* A decorative illustration of the real Publish button, not a real
          control — the whole demo is wrapped `aria-hidden` by the caller. */}
      <div
        className={`hiw-pub ${phase === 'press' ? 'press' : ''} ${
          phase === 'done' ? 'done' : ''
        }`}
      >
        {phase === 'loading' ? (
          <span className="hiw-spin" />
        ) : (
          <span>
            {phase === 'done' ? tDemo('published') : tPublish('publish')}
          </span>
        )}
      </div>
    </div>
  )
}

const STEP_DEMOS = {
  step1: Step1Demo,
  step2: Step2Demo,
  step3: Step3Demo,
}

// The three-step setup walkthrough (Sketch prototype live-updates-onboarding
// v29), each paired with a self-playing mini-card demo — a merchant toggling
// stages on, typing a message, and publishing, looping continuously (frozen
// on the settled end-state when the visitor prefers reduced motion).
export interface HowItWorksSectionProps {
  /** The merchant's own logo, shown on the demo cards the way the real
   * preview shows it — the prototype brands these with its demo merchant. */
  appIconUrl: string | undefined
}

const HowItWorksSection = ({ appIconUrl }: HowItWorksSectionProps) => {
  const [css] = useStyletron()
  const demo = useHowItWorksDemo(tDemo('message'))

  return (
    <div>
      <Text.Header3
        tag="h2"
        className={css({ margin: '0 0 4px', paddingTop: '8px' })}
      >
        {t('title')}
      </Text.Header3>
      <Text.BodyText4
        color="grey70"
        className={css({ display: 'block', marginBottom: '16px' })}
      >
        {t('subtitle')}
      </Text.BodyText4>
      <div
        className={css({
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${STEP_MIN_WIDTH}, 1fr))`,
          gap: STEP_GAP,
          padding: '8px 24px',
          borderRadius: '12px',
          backgroundColor: palette.white,
          border: `1px solid ${palette.grey30}`,
        })}
      >
        {STEP_KEYS.map((key, index) => {
          const Demo = STEP_DEMOS[key]
          return (
            <div
              key={key}
              className={css({
                padding: '16px 6px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              })}
            >
              <div
                aria-hidden="true"
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: palette.spearmintTint,
                  color: palette.spearmint60,
                  fontWeight: 700,
                  marginBottom: '12px',
                })}
              >
                {index + 1}
              </div>
              {/* Fixed-height copy block so a step whose body wraps to an
                  extra line doesn't push its demo card below the other two
                  (Sketch prototype's `.how .step .sx` min-height). */}
              <div className={css({ minHeight: STEP_COPY_HEIGHT })}>
                <Text.BodyText4
                  bold
                  color="grey90"
                  className={css({ display: 'block' })}
                >
                  {t(`${key}_title`)}
                </Text.BodyText4>
                <Text.BodyText4
                  color="grey70"
                  className={css({ display: 'block', marginTop: '4px' })}
                >
                  {t(`${key}_body`)}
                </Text.BodyText4>
              </div>
              <div
                aria-hidden="true"
                className={css({
                  width: '100%',
                  marginTop: '18px',
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                })}
              >
                <Demo demo={demo} appIconUrl={appIconUrl} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default HowItWorksSection
